import { type ReactNode, useId, useState } from "react";
import { Chip } from "./Chip";
import "./ui.css";

interface SectionProps {
	title: string;
	level?: 2 | 3;
	/** A line under the title saying what the section holds. */
	subtitle?: ReactNode;
	actions?: ReactNode;
	/** Puts the body behind a Hide / Show disclosure on the heading. */
	collapsible?: boolean;
	/** Which way a collapsible section starts; ignored without it. */
	defaultOpen?: boolean;
	children: ReactNode;
}

export function Section({
	title,
	level = 3,
	subtitle,
	actions,
	collapsible = false,
	defaultOpen = true,
	children,
}: SectionProps) {
	const Heading = level === 2 ? "h2" : "h3";
	const titleId = useId();
	const bodyId = useId();
	const [open, setOpen] = useState(defaultOpen);
	const hidden = collapsible && !open;

	// The disclosure button sits inside the heading, as FinancialSnapshotCard
	// does, so the heading stays in the outline. Its name is the title alone;
	// aria-expanded carries the state, so the Hide / Show pill is hidden from
	// it. The subtitle stays outside the button: it is a ReactNode and may hold
	// a link, which must not nest inside one.
	const heading = collapsible ? (
		<Heading className="ui-section-header-title">
			<button
				type="button"
				className="ui-section-headbtn"
				aria-expanded={open}
				aria-controls={bodyId}
				aria-labelledby={titleId}
				onClick={() => setOpen((o) => !o)}
			>
				<span id={titleId}>{title}</span>
				<Chip variant="grey" className="ui-section-pill" aria-hidden="true">
					{open ? "Hide" : "Show"}
				</Chip>
			</button>
		</Heading>
	) : (
		<Heading className="ui-section-header-title">{title}</Heading>
	);

	return (
		<section className="ui-section">
			<div className="ui-section-head">
				{subtitle != null ? (
					<div>
						{heading}
						<div className="ui-section-subtitle">{subtitle}</div>
					</div>
				) : (
					heading
				)}
				{actions && <div className="ui-section-header-actions">{actions}</div>}
			</div>
			{/* Kept mounted so aria-controls always points at it. */}
			<div id={bodyId} className="ui-section-body" hidden={hidden}>
				{children}
			</div>
		</section>
	);
}
